/**
 * EventDetail — the read-only event detail card shown in a popover when a
 * calendar event is clicked. Mirrors the Google-Calendar event popover: a
 * colored title block with the date/time, then icon-aligned rows for location,
 * description, guests and status.
 *
 * View-only by design — the only control is a close button. No edit / delete /
 * RSVP actions are wired up.
 */

import type { ReactNode } from 'react';
import { X, MapPin, AlignLeft, Users, Video } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PopoverClose } from '@/components/ui/popover';
import { CopyButton } from '@/components/ui/CopyButton';
import { MarkdownContent } from '@/components/ui/markdown-content';
import {
  formatDayLong,
  formatTimeRange,
  statusAccent,
  detectConference,
  formatMailbox,
  normalizeDescriptionMarkup,
} from './calendar.helpers';
import type { NormalizedEvent } from './calendar.types';
import { cleanDisplayText } from '@/utils/display-text';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

/** A leading-icon row with a fixed-width gutter so every line stays aligned.
 *  The icon is centered within a first-line-height box (`h-6`, matched to the
 *  optical center of the value text) and `self-start` pins it to that first
 *  line on multi-line rows, so icons line up with the text across the card. */
function DetailRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span
        className="flex h-6 w-5 shrink-0 items-center justify-center self-start text-muted-foreground"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export interface EventDetailProps {
  event: NormalizedEvent;
  hour12?: boolean;
}

export function EventDetail({ event, hour12 }: EventDetailProps) {
  const accent = statusAccent(event.status);
  const owners = event.owners ?? [];
  const conference = detectConference(event);
  const guestEmails = owners
    .filter((o) => !!o.email)
    .map((o) => formatMailbox(o.name, o.email as string))
    .join(', ');

  return (
    <div className="relative flex flex-col" data-testid="calendar-event-detail">
      {/* Dismiss control — a zero-height sticky wrapper keeps the close button
          pinned to the top-right while the body scrolls, without reserving a
          full header row above the title (which read as an empty top gap). */}
      <div className="sticky top-0 z-10 h-0">
        <PopoverClose
          className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-popover/80 text-muted-foreground backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground focus-ring"
          aria-label="Close"
        >
          <X className="size-4" />
        </PopoverClose>
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5 pt-4">
        {/* Title + date/time — color swatch shares the rows' icon gutter so
            the title text lines up with the key/value rows below. */}
        <div className="flex gap-3">
          <span className="mt-1.5 flex w-5 shrink-0 justify-center" aria-hidden>
            <span
              className={cn('size-3.5 rounded-[4px]', !event.color && (accent?.dot ?? 'bg-muted-foreground'))}
              style={event.color ? { backgroundColor: event.color } : undefined}
            />
          </span>
          <div className="min-w-0 flex-1 pr-8">
            <h3 className="text-lg font-medium leading-snug text-foreground">{cleanDisplayText(event.title)}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {formatDayLong(event.startDate)} · {formatTimeRange(event, hour12)}
            </p>
          </div>
        </div>

        {/* Video-conference join link */}
        {conference && (
          <DetailRow icon={<Video className="size-4" />}>
            <a
              href={conference.url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-words text-sm font-medium text-primary hover:underline focus-ring rounded-sm"
            >
              {conference.label}
            </a>
          </DetailRow>
        )}

        {/* Location (suppressed when it's just the conference URL we surfaced above) */}
        {event.location && conference?.source !== 'location' && (
          <DetailRow icon={<MapPin className="size-4" />}>
            <p className="text-sm text-foreground">{event.location}</p>
          </DetailRow>
        )}

        {/* Description — calendar descriptions are commonly HTML (Google
            Calendar) or, from agents, markdown. MarkdownContent renders both
            (embedded raw HTML via rehype-raw) and sanitizes the output, which
            matters here since the text is external/untrusted. */}
        {event.description && (
          <DetailRow icon={<AlignLeft className="size-4" />}>
            <MarkdownContent
              size="sm"
              // Drop the prose top/bottom margins on the outer blocks so the
              // first line aligns with the leading icon and the row doesn't add
              // stray trailing space.
              className="break-words text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a:has(img)_svg]:hidden [&_a]:break-words"
            >
              {normalizeDescriptionMarkup(event.description)}
            </MarkdownContent>
          </DetailRow>
        )}

        {/* Guests */}
        {owners.length > 0 && (
          <DetailRow icon={<Users className="size-4" />}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-foreground">
                {owners.length} {owners.length === 1 ? 'guest' : 'guests'}
              </p>
              {guestEmails && (
                <CopyButton
                  value={guestEmails}
                  size="md"
                  title="Copy guest emails"
                  successLabel="Copied"
                />
              )}
            </div>
            <ul className="mt-2 flex flex-col gap-2">
              {owners.map((o, i) => (
                <li key={`${o.name}-${i}`} className="flex items-center gap-2">
                  <Avatar className="h-6 w-6" title={o.name}>
                    {o.avatarUrl && <AvatarImage src={o.avatarUrl} alt={o.name} />}
                    <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
                      {initials(o.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate text-sm text-foreground">{o.name}</span>
                </li>
              ))}
            </ul>
          </DetailRow>
        )}

        {/* Status */}
        {accent && (
          <DetailRow icon={<span className={cn('size-2.5 rounded-full', accent.dot)} />}>
            <p className={cn('text-sm font-medium', accent.text)}>{accent.label}</p>
          </DetailRow>
        )}
      </div>
    </div>
  );
}
